import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { fabric } from 'fabric';
import { jsPDF } from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import ParticularService from '../services/ParticularService';
import particularesApiService from '../services/ParticularesApiService';
import apiService from '../services/ApiService';
import Alert from './common/Alert';
import UserSessionMenu from './common/UserSessionMenu';
import useAulaHubPresence from '../hooks/useAulaHubPresence';
import { AulaEmptyState, AulaNav, AulaOverview, AulaSection } from './common/AulaShell';
import { isAdminColegio } from '../utils/adminMenu';
import logoAnimacion from '../assets/logos/logoAnimacion.png';
import './AulaPublicPage.css';
import './NewDashboard.css';
import './ParticularDashboard.css';

const BOARD_MIN_ZOOM = 0.3;
const BOARD_MAX_ZOOM = 3.5;
const BOARD_OBJECT_JSON_PROPS = [
  'src',
  'left',
  'top',
  'scaleX',
  'scaleY',
  'angle',
  'width',
  'height',
  'text',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'underline',
  'fill',
  'textAlign',
  'charSpacing',
  'lineHeight',
  'editable'
];

const getId = (item) => String(item?.id || item?.Id || '');
const getName = (item, fallback = 'Sin nombre') => item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || fallback;
const getDescripcion = (item) => item?.descripcion || item?.Descripcion || '';
const getList = (item, lowerKey, upperKey) => {
  const value = item?.[lowerKey] || item?.[upperKey] || [];
  return Array.isArray(value) ? value : [];
};
const getVisibleFlag = (item, lowerKey, upperKey, fallback = true) => {
  if (!item) return fallback;
  if (typeof item[lowerKey] === 'boolean') return item[lowerKey];
  if (typeof item[upperKey] === 'boolean') return item[upperKey];
  return fallback;
};
const getAssistantId = (item) => item?.idOpenAIAssistant || item?.IdOpenAIAssistant || '';
const getAssistantName = (item) => (
  item?.nombreVisible ||
  item?.NombreVisible ||
  item?.gptClasicoNombre ||
  item?.GPTClasicoNombre ||
  item?.nombre ||
  item?.Nombre ||
  'Asistente IA'
);
const getAssistantUrl = (item) => item?.gptClasicoURLPublica || item?.GPTClasicoURLPublica || item?.gptClasicoUrlPublica || item?.GptClasicoUrlPublica || '';
const getAssistantDescription = (item) => item?.gptClasicoDescripcion || item?.GPTClasicoDescripcion || item?.descripcion || item?.Descripcion || 'Asistente IA';
const getBoardObjectJson = (object) => object.toJSON(BOARD_OBJECT_JSON_PROPS);
const clampBoardZoom = (zoom) => Math.min(Math.max(zoom, BOARD_MIN_ZOOM), BOARD_MAX_ZOOM);

const getHubBaseUrl = () => (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  apiService.baseURL ||
  window.location.origin
).replace(/\/api\/?$/, '').replace(/\/$/, '');

const getBearerToken = () => {
  const header = apiService.getAuthHeaders()?.Authorization || '';
  return header.replace(/^Bearer\s+/i, '');
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeChatExportText = (value) => (
  String(value ?? '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
);

const getLinkHostname = (url) => {
  try {
    return new URL(String(url || '')).hostname.replace(/^www\./i, '');
  } catch (_) {
    return '';
  }
};

const TagPill = ({ etiqueta }) => (
  <span className="particular-tag" style={{ '--tag-color': etiqueta?.color || etiqueta?.Color || '#2563eb' }}>
    {getName(etiqueta, 'Etiqueta')}
  </span>
);

const ParticularAulaPage = () => {
  const { aulaId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const connectionRef = useRef(null);
  const boardConnectionRef = useRef(null);
  const callConnectionRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatInputRef = useRef(null);
  const boardCanvasRef = useRef(null);
  const boardContainerRef = useRef(null);
  const fabricInstanceRef = useRef(null);
  const imageInputRef = useRef(null);
  const boardActionSourceRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const boardStartedByRef = useRef('');
  const telemetrySessionIdRef = useRef(`particular-aula-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const pageViewTrackedRef = useRef('');
  const boardPanModeRef = useRef(false);
  const boardSelectModeRef = useRef(false);
  const boardZoomRef = useRef(1);
  const boardBrushRef = useRef({ color: '#0f172a', width: 3, eraser: false, eraserWidth: 18 });
  const isBoardPanningRef = useRef(false);
  const boardLastPointRef = useRef(null);
  const pendingBoardActionsRef = useRef([]);
  const audioCtxRef = useRef(null);
  const chatMutedRef = useRef(false);
  const notificationSoundRef = useRef(
    typeof Audio !== 'undefined'
      ? new Audio(
          'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQQAAAAA////2/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7///8='
        )
      : null
  );

  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [aula, setAula] = useState(null);
  const [curso, setCurso] = useState(null);
  const [assignedAssistants, setAssignedAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');

  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState('');
  const [selectedLinkResource, setSelectedLinkResource] = useState(null);
  const [linkViewerLoading, setLinkViewerLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [messagesSearch, setMessagesSearch] = useState('');
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [replyParent, setReplyParent] = useState(null);
  const [replyHtml, setReplyHtml] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [newMessageTitle, setNewMessageTitle] = useState('');
  const [newMessageHtml, setNewMessageHtml] = useState('');
  const [newMessageSending, setNewMessageSending] = useState(false);

  const [activeSection, setActiveSection] = useState('inicio');

  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatUsers, setChatUsers] = useState([]);
  const [chatConnecting, setChatConnecting] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMuted, setChatMuted] = useState(false);

  const [boardOpen, setBoardOpen] = useState(false);
  const [boardConnecting, setBoardConnecting] = useState(false);
  const [boardUsers, setBoardUsers] = useState([]);
  const [boardStartedBy, setBoardStartedBy] = useState('');
  const [boardStatusMsg, setBoardStatusMsg] = useState('Nadie inició la pizarra aún.');
  const [boardColor, setBoardColor] = useState('#0f172a');
  const [boardWidth, setBoardWidth] = useState(3);
  const [boardTextSize, setBoardTextSize] = useState(28);
  const [boardEraser, setBoardEraser] = useState(false);
  const [boardEraserWidth, setBoardEraserWidth] = useState(18);
  const [boardPanMode, setBoardPanMode] = useState(false);
  const [boardSelectMode, setBoardSelectMode] = useState(false);
  const [boardZoom, setBoardZoom] = useState(1);
  const [boardExporting, setBoardExporting] = useState(false);
  const [callInfo, setCallInfo] = useState(null);
  const [callConnecting, setCallConnecting] = useState(false);
  const [callNameModalOpen, setCallNameModalOpen] = useState(false);
  const [callNameInput, setCallNameInput] = useState('');

  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const isAlumnoParticular = roleName.includes('alumnoparticular') || (roleName.includes('alumno') && roleName.includes('particular'));
  const isGeneralAdmin = roleName.includes('admin') && !isAdminColegio(user);
  const canSeeEtiquetas = !isAlumnoParticular && !isGeneralAdmin;

  const participantDisplayName = useMemo(() => {
    const fullName = `${user?.nombre || ''} ${user?.apellido || ''}`.trim();
    return fullName || user?.displayName || user?.name || user?.email || (isAlumnoParticular ? 'Alumno' : 'Usuario');
  }, [isAlumnoParticular, user]);

  const aulaNombre = aula ? getName(aula, 'Aula particular') : 'Aula particular';
  const aulaDescripcion = getDescripcion(aula) || getDescripcion(curso);
  const etiquetas = useMemo(() => getList(curso, 'etiquetas', 'Etiquetas'), [curso]);
  const backPath = isAlumnoParticular ? '/particular/alumno-panel' : '/particular';

  const filesEnabled = getVisibleFlag(aula, 'mostrarArchivos', 'MostrarArchivos', true);
  const linksEnabled = getVisibleFlag(aula, 'mostrarLinks', 'MostrarLinks', true);
  const boardEnabled = getVisibleFlag(aula, 'mostrarPizarra', 'MostrarPizarra', true);
  const chatEnabled = getVisibleFlag(aula, 'mostrarChat', 'MostrarChat', true);
  const videoEnabled = getVisibleFlag(aula, 'mostrarVideollamada', 'MostrarVideollamada', true);
  const messagesEnabled = getVisibleFlag(aula, 'mostrarForo', 'MostrarForo', true);
  const assistantsEnabled = getVisibleFlag(aula, 'mostrarAsistentesIA', 'MostrarAsistentesIA', true);
  const jitsiBase = process.env.REACT_APP_JITSI_BASE || 'https://meet.jit.si';

  const emitTelemetry = useCallback((tipoEvento, metadata = {}) => {
    if (!aulaId) return Promise.resolve();
    return particularesApiService.createAulaTelemetry(aulaId, {
      tipoEvento,
      sessionId: telemetrySessionIdRef.current,
      metadata: {
        ...metadata,
        displayName: participantDisplayName,
        aulaNombre
      }
    }).catch(() => {});
  }, [aulaId, aulaNombre, participantDisplayName]);

  const applyBoardPresence = useCallback((users) => {
    const cleaned = Array.isArray(users)
      ? users.filter((name) => typeof name === 'string' && name.trim() && !name.startsWith('__observer__'))
      : [];

    setBoardUsers(cleaned);

    if (cleaned.length > 0) {
      const starter = boardStartedByRef.current || cleaned[0];
      setBoardStartedBy(starter);
      setBoardStatusMsg(`${starter} inició una pizarra colaborativa.`);
    } else {
      setBoardStartedBy('');
      setBoardStatusMsg('Nadie inició la pizarra aún.');
    }

    return cleaned;
  }, []);

  // Presencia sin unirse: permite ver que el chat o la pizarra ya están en vivo.
  const chatPresenceUsers = useAulaHubPresence({
    enabled: Boolean(aula) && chatEnabled && !chatOpen && !chatConnecting,
    hubUrl: `${getHubBaseUrl()}/hubs/particular-aula-chat`,
    aulaId,
    accessTokenFactory: getBearerToken
  });
  const boardPresenceUsers = useAulaHubPresence({
    enabled: Boolean(aula) && boardEnabled && !boardOpen && !boardConnecting,
    hubUrl: `${getHubBaseUrl()}/hubs/particular-aula-board`,
    aulaId,
    accessTokenFactory: getBearerToken
  });
  const chatLive = chatOpen || chatPresenceUsers.length > 0;

  useEffect(() => {
    if (boardOpen || boardConnecting) return;
    applyBoardPresence(boardPresenceUsers);
  }, [applyBoardPresence, boardConnecting, boardOpen, boardPresenceUsers]);

  const handleChangeParticular = useCallback(() => {
    ParticularService.setActiveParticular(null);
    navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
  }, [navigate]);

  const loadAssignedAssistants = useCallback(async () => {
    if (!aulaId) return;
    try {
      const response = await particularesApiService.getAulaAsistentesIA(aulaId);
      const items = getList(response, 'asistentes', 'Asistentes')
        .filter((assistant) => {
          const assigned = assistant?.estaAsignadoAlAula ?? assistant?.EstaAsignadoAlAula;
          return assigned === true;
        });
      setAssignedAssistants(items);
      setSelectedAssistant((current) => {
        if (!current) return items[0] || null;
        return items.find((item) => getAssistantId(item) === getAssistantId(current)) || items[0] || null;
      });
    } catch (_) {
      setAssignedAssistants([]);
      setSelectedAssistant(null);
    }
  }, [aulaId]);

  const loadFiles = useCallback(async () => {
    if (!aulaId || !filesEnabled) return;
    setFilesLoading(true);
    setFilesError('');
    try {
      const response = await particularesApiService.getAulaArchivos(aulaId);
      const items = response?.items || response?.Items || response?.archivos || response?.Archivos || response || [];
      setFiles(Array.isArray(items) ? items : []);
    } catch (error) {
      setFilesError(error.message || 'No pudimos obtener los archivos.');
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [aulaId, filesEnabled]);

  const loadLinks = useCallback(async () => {
    if (!aulaId || !linksEnabled) return;
    setLinksLoading(true);
    setLinksError('');
    try {
      const response = await particularesApiService.getAulaLinks(aulaId);
      const items = response?.items || response?.Items || response?.links || response?.Links || response || [];
      setLinks(Array.isArray(items) ? items : []);
    } catch (error) {
      setLinksError(error.message || 'No pudimos obtener los links.');
      setLinks([]);
    } finally {
      setLinksLoading(false);
    }
  }, [aulaId, linksEnabled]);

  const loadMessages = useCallback(async (search = messagesSearch) => {
    if (!aulaId || !messagesEnabled) return;
    setMessagesLoading(true);
    setMessagesError('');
    try {
      const response = await particularesApiService.getAulaMensajes(aulaId, {
        start: 0,
        end: 149,
        search: search.trim(),
        scope: 'todos'
      });
      const items = response?.items || response?.Items || response?.mensajes || response?.Mensajes || response || [];
      setMessages(Array.isArray(items) ? items : []);
      setMessagesTotal(response?.total ?? response?.Total ?? (Array.isArray(items) ? items.length : 0));
    } catch (error) {
      setMessagesError(error.message || 'No pudimos obtener los mensajes.');
      setMessages([]);
      setMessagesTotal(0);
    } finally {
      setMessagesLoading(false);
    }
  }, [aulaId, messagesEnabled, messagesSearch]);

  const loadAula = useCallback(async () => {
    if (!aulaId) return;
    setIsLoading(true);
    setAlert((prev) => ({ ...prev, show: false }));

    try {
      let aulaFound = null;
      let cursoFound = null;

      if (isAlumnoParticular) {
        const panel = await particularesApiService.getAlumnoPanel();
        const cursos = getList(panel, 'cursos', 'Cursos');
        cursos.some((cursoItem) => {
          const aulas = getList(cursoItem, 'aulas', 'Aulas');
          aulaFound = aulas.find((item) => getId(item) === String(aulaId));
          if (aulaFound) {
            cursoFound = cursoItem;
            return true;
          }
          return false;
        });
      } else {
        const [aulasRes, cursosRes] = await Promise.all([
          particularesApiService.getAulas(),
          particularesApiService.getCursos()
        ]);
        const aulas = getList(aulasRes, 'aulas', 'Aulas');
        const cursos = getList(cursosRes, 'cursos', 'Cursos');
        aulaFound = aulas.find((item) => getId(item) === String(aulaId));
        const cursoId = aulaFound?.idCursoParticular || aulaFound?.IdCursoParticular;
        cursoFound = cursos.find((item) => getId(item) === String(cursoId));
      }

      if (!aulaFound) {
        throw new Error('Aula no encontrada.');
      }

      setAula(aulaFound);
      setCurso(cursoFound);
    } catch (error) {
      setAlert({
        show: true,
        title: 'No pudimos abrir el aula',
        message: error.message || 'Intenta nuevamente.',
        type: 'error'
      });
      setAula(null);
      setCurso(null);
      setAssignedAssistants([]);
    } finally {
      setIsLoading(false);
    }
  }, [aulaId, isAlumnoParticular]);

  useEffect(() => {
    loadAula();
  }, [loadAula]);

  useEffect(() => {
    if (!aula) return;
    if (assistantsEnabled) void loadAssignedAssistants();
    if (filesEnabled) void loadFiles();
    if (linksEnabled) void loadLinks();
    if (messagesEnabled) void loadMessages();
  }, [aula, assistantsEnabled, filesEnabled, linksEnabled, messagesEnabled, loadAssignedAssistants, loadFiles, loadLinks, loadMessages]);

  useEffect(() => {
    if (!aula || !aulaId) return;
    const key = `${aulaId}:${telemetrySessionIdRef.current}`;
    if (pageViewTrackedRef.current === key) return;
    pageViewTrackedRef.current = key;
    void emitTelemetry('aula.page_view', { origen: 'aula_particular' });
  }, [aula, aulaId, emitTelemetry]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    boardStartedByRef.current = boardStartedBy;
  }, [boardStartedBy]);

  useEffect(() => {
    chatMutedRef.current = chatMuted;
  }, [chatMuted]);

  useEffect(() => {
    boardPanModeRef.current = boardPanMode;
    boardSelectModeRef.current = boardSelectMode;
    boardBrushRef.current = { color: boardColor, width: boardWidth, eraser: boardEraser, eraserWidth: boardEraserWidth };
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    instance.isDrawingMode = !boardPanMode && !boardSelectMode;
    instance.selection = boardSelectMode;
    if (instance.freeDrawingBrush) {
      instance.freeDrawingBrush.color = boardEraser ? '#ffffff' : boardColor;
      instance.freeDrawingBrush.width = boardEraser ? boardEraserWidth : boardWidth;
    }
    instance.forEachObject((object) => {
      const editable = ['image', 'i-text', 'textbox', 'text'].includes(object?.type);
      object.selectable = Boolean(boardSelectMode && editable);
      object.evented = Boolean(boardSelectMode && editable);
      if ('editable' in object) {
        object.editable = Boolean(boardSelectMode && editable);
      }
    });
    if (!boardSelectMode && instance.getActiveObject()) {
      instance.discardActiveObject();
      instance.requestRenderAll();
    }
  }, [boardColor, boardEraser, boardEraserWidth, boardPanMode, boardSelectMode, boardWidth]);

  const disposeBoardCanvas = useCallback(() => {
    if (fabricInstanceRef.current) {
      fabricInstanceRef.current.dispose();
      fabricInstanceRef.current = null;
    }
    boardZoomRef.current = 1;
    setBoardZoom(1);
  }, []);

  useEffect(() => () => {
    connectionRef.current?.stop().catch(() => {});
    boardConnectionRef.current?.stop().catch(() => {});
    callConnectionRef.current?.stop().catch(() => {});
    disposeBoardCanvas();
  }, [disposeBoardCanvas]);

  useEffect(() => {
    const setupCallHub = async () => {
      if (!videoEnabled || !aulaId || callConnectionRef.current) return;

      try {
        const connection = new HubConnectionBuilder()
          .withUrl(`${getHubBaseUrl()}/hubs/particular-aula-call`, {
            withCredentials: true,
            accessTokenFactory: getBearerToken
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();

        connection.on('VideoCallStarted', (payload) => {
          if (!payload) return;
          setCallInfo({
            startedBy: payload.startedBy || 'Usuario',
            roomUrl: payload.roomUrl,
            startedAt: payload.startedAt
          });
        });

        connection.on('VideoCallEnded', () => {
          setCallInfo(null);
        });

        await connection.start();
        await connection.invoke('JoinAulaCall', aulaId);
        callConnectionRef.current = connection;
      } catch (error) {
        setMessagesError(error.message || 'No pudimos conectar la videollamada.');
      }
    };

    void setupCallHub();

    return () => {
      if (!videoEnabled && callConnectionRef.current) {
        callConnectionRef.current.stop().catch(() => {});
        callConnectionRef.current = null;
      }
    };
  }, [aulaId, videoEnabled]);

  const sendBoardAction = useCallback(async (action) => {
    if (!boardConnectionRef.current || !aulaId) return;
    try {
      await boardConnectionRef.current.invoke(
        'SendBoardAction',
        aulaId,
        participantDisplayName,
        JSON.stringify({ ...action, sourceId: boardActionSourceRef.current })
      );
    } catch (_) {
      setMessagesError('No pudimos sincronizar la pizarra.');
    }
  }, [aulaId, participantDisplayName]);

  const syncBoardZoom = (instance) => {
    if (!instance) return;
    const nextZoom = Math.round(clampBoardZoom(instance.getZoom()) * 100) / 100;
    boardZoomRef.current = nextZoom;
    setBoardZoom(nextZoom);
  };

  const zoomBoardByFactor = (factor) => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    const center = new fabric.Point(instance.getWidth() / 2, instance.getHeight() / 2);
    instance.zoomToPoint(center, clampBoardZoom(instance.getZoom() * factor));
    syncBoardZoom(instance);
    instance.requestRenderAll();
  };

  const handleRemoteBoardObject = useCallback((data) => {
    const instance = fabricInstanceRef.current;

    if (data.type === 'snapshot-request') {
      if (!instance) {
        pendingBoardActionsRef.current.push(data);
        return;
      }
      const objects = instance.getObjects() || [];
      if (objects.length > 0) {
        const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
        snapshot.background = instance.backgroundColor || '#ffffff';
        sendBoardAction({ type: 'snapshot', snapshot });
      }
      return;
    }

    if (!instance) {
      pendingBoardActionsRef.current.push(data);
      return;
    }

    if (data.type === 'snapshot' && data.snapshot) {
      instance.loadFromJSON(data.snapshot, () => {
        if (data.snapshot.background) {
          instance.backgroundColor = data.snapshot.background;
        }
        instance.renderAll();
      });
      return;
    }

    if (data.type === 'clear') {
      instance.clear();
      instance.backgroundColor = '#ffffff';
      instance.renderAll();
      return;
    }

    if (data.object) {
      fabric.util.enlivenObjects([data.object], (objects) => {
        objects.forEach((object) => {
          object.selectable = false;
          object.evented = false;
          instance.add(object);
        });
        instance.renderAll();
      });
    }
  }, [sendBoardAction]);

  const initBoardCanvas = useCallback(async () => {
    const canvasEl = boardCanvasRef.current;
    const container = boardContainerRef.current;
    if (!canvasEl || !container) return;

    if (fabricInstanceRef.current) {
      disposeBoardCanvas();
    }

    const instance = new fabric.Canvas(canvasEl, {
      isDrawingMode: !boardPanModeRef.current && !boardSelectModeRef.current,
      selection: boardSelectModeRef.current,
      backgroundColor: '#ffffff'
    });
    const { width, height } = container.getBoundingClientRect();
    instance.setWidth(width);
    instance.setHeight(Math.max(height, window.innerWidth < 768 ? 420 : 560));
    instance.upperCanvasEl.style.touchAction = 'none';

    // El pincel se lee de un ref: si initBoardCanvas dependiera del color o el grosor,
    // cada cambio recrearía el lienzo vacío y el siguiente trazo pisaría la pizarra de todos.
    if (instance.freeDrawingBrush) {
      const brush = boardBrushRef.current;
      instance.freeDrawingBrush.color = brush.eraser ? '#ffffff' : brush.color;
      instance.freeDrawingBrush.width = brush.eraser ? brush.eraserWidth : brush.width;
    }

    instance.on('path:created', (event) => {
      const object = event.path;
      if (!object) return;
      object.selectable = false;
      sendBoardAction({ type: 'fabric-path', object: object.toJSON(['path', 'stroke', 'strokeWidth', 'fill', 'strokeLineCap', 'strokeLineJoin']) });
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    });

    instance.on('object:modified', (event) => {
      if (!event.target) return;
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(event.target) });
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    });

    instance.on('text:changed', (event) => {
      if (!event.target) return;
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(event.target) });
    });

    instance.on('mouse:down', (event) => {
      if (!boardPanModeRef.current) return;
      isBoardPanningRef.current = true;
      const e = event.e;
      boardLastPointRef.current = { x: e.clientX, y: e.clientY };
      instance.isDrawingMode = false;
    });

    instance.on('mouse:move', (event) => {
      if (!isBoardPanningRef.current || !boardPanModeRef.current) return;
      const e = event.e;
      const last = boardLastPointRef.current;
      if (!last) return;
      instance.relativePan(new fabric.Point(e.clientX - last.x, e.clientY - last.y));
      boardLastPointRef.current = { x: e.clientX, y: e.clientY };
    });

    instance.on('mouse:up', () => {
      isBoardPanningRef.current = false;
      boardLastPointRef.current = null;
      instance.isDrawingMode = !boardPanModeRef.current && !boardSelectModeRef.current;
    });

    instance.on('mouse:wheel', (event) => {
      const delta = event.e.deltaY;
      const zoom = clampBoardZoom(instance.getZoom() * (0.999 ** delta));
      instance.zoomToPoint(new fabric.Point(event.e.offsetX, event.e.offsetY), zoom);
      syncBoardZoom(instance);
      event.e.preventDefault();
      event.e.stopPropagation();
    });

    fabricInstanceRef.current = instance;
    syncBoardZoom(instance);
    instance.renderAll();

    const pendingActions = pendingBoardActionsRef.current.splice(0);
    pendingActions.forEach((action) => handleRemoteBoardObject(action));
  }, [disposeBoardCanvas, handleRemoteBoardObject, sendBoardAction]);

  useEffect(() => {
    if (!boardOpen) {
      disposeBoardCanvas();
      return;
    }

    void initBoardCanvas();
  }, [boardOpen, disposeBoardCanvas, initBoardCanvas]);

  useEffect(() => {
    if (!boardOpen) return undefined;
    const resize = () => {
      const instance = fabricInstanceRef.current;
      const container = boardContainerRef.current;
      if (!instance || !container) return;
      const { width, height } = container.getBoundingClientRect();
      // La sección oculta mide 0: se recalcula al volver a mostrarla.
      if (!width) return;
      instance.setWidth(width);
      instance.setHeight(Math.max(height, window.innerWidth < 768 ? 420 : 560));
      instance.renderAll();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [activeSection, boardOpen]);

  const primeNotificationSound = async () => {
    try {
      if (!audioCtxRef.current && typeof window !== 'undefined') {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          audioCtxRef.current = new AudioContextClass();
        }
      }

      if (audioCtxRef.current?.state === 'suspended') {
        await audioCtxRef.current.resume();
      }

      if (notificationSoundRef.current) {
        notificationSoundRef.current.volume = 0.25;
        await notificationSoundRef.current.play().catch(() => {});
        notificationSoundRef.current.pause();
        notificationSoundRef.current.currentTime = 0;
        notificationSoundRef.current.muted = false;
      }
    } catch (_) {}
  };

  const playNotificationSound = async () => {
    if (chatMutedRef.current) return;

    try {
      const ctx = audioCtxRef.current;
      if (ctx) {
        if (ctx.state === 'suspended') await ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(640, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(880, ctx.currentTime + 0.18);
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.004, ctx.currentTime + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.18);
        return;
      }

      if (notificationSoundRef.current) {
        notificationSoundRef.current.currentTime = 0;
        notificationSoundRef.current.volume = 0.25;
        notificationSoundRef.current.play().catch(() => {});
      }
    } catch (_) {}
  };

  const startChatConnection = async () => {
    if (!aulaId || !participantDisplayName || connectionRef.current) return;
    setChatConnecting(true);
    setMessagesError('');
    try {
      await primeNotificationSound();
      const connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/particular-aula-chat`, {
          withCredentials: true,
          accessTokenFactory: getBearerToken
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('ReceiveMessage', (payload) => {
        setChatMessages((prev) => [...prev, { ...payload, tipo: 'mensaje' }]);
        void playNotificationSound();
      });
      connection.on('UserJoined', (name, fechaUtc) => {
        setChatMessages((prev) => [...prev, { autor: 'sistema', contenido: `${name} se sumó al chat`, fechaUtc, tipo: 'sistema' }]);
        setChatUsers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.includes(name) ? list : [...list, name];
        });
      });
      connection.on('UserLeft', (name, fechaUtc) => {
        setChatMessages((prev) => [...prev, { autor: 'sistema', contenido: `${name} salió del chat`, fechaUtc, tipo: 'sistema' }]);
        setChatUsers((prev) => (Array.isArray(prev) ? prev.filter((item) => item !== name) : []));
      });
      connection.on('ConnectedUsers', (users) => {
        setChatUsers(Array.isArray(users) ? users : []);
      });

      await connection.start();
      connectionRef.current = connection;
      setChatOpen(true);
      await connection.invoke('JoinAula', aulaId, participantDisplayName);
      setChatUsers((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.includes(participantDisplayName) ? list : [...list, participantDisplayName];
      });
      try {
        const users = await connection.invoke('GetConnectedUsers', aulaId);
        if (Array.isArray(users)) setChatUsers(users);
      } catch (_) {}
      void emitTelemetry('aula.chat_started', { origen: 'chat_grupal' });
    } catch (error) {
      try {
        await connectionRef.current?.stop();
      } catch (_) {}
      connectionRef.current = null;
      setChatOpen(false);
      setMessagesError(error.message || 'No pudimos conectar al chat del aula.');
    } finally {
      setChatConnecting(false);
    }
  };

  const leaveChat = async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (_) {}
      connectionRef.current = null;
    }
    setChatOpen(false);
    setChatMessages([]);
    setChatUsers([]);
  };

  const handleSendChatMessage = async () => {
    if (!connectionRef.current || !chatInput.trim()) return;
    const text = chatInput.trim();
    setChatInput('');
    if (chatInputRef.current) {
      chatInputRef.current.style.height = '40px';
    }

    try {
      await connectionRef.current.invoke('SendMessage', aulaId, participantDisplayName, text);
    } catch (_) {
      setMessagesError('No pudimos enviar el mensaje del chat.');
    }
  };

  const iniciarVideollamada = async (nombreParam) => {
    if (!aulaId) return;

    let connection = callConnectionRef.current;
    setCallConnecting(true);
    setMessagesError('');
    try {
      if (!connection) {
        connection = new HubConnectionBuilder()
          .withUrl(`${getHubBaseUrl()}/hubs/particular-aula-call`, {
            withCredentials: true,
            accessTokenFactory: getBearerToken
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();

        connection.on('VideoCallStarted', (payload) => {
          if (!payload) return;
          setCallInfo({
            startedBy: payload.startedBy || 'Usuario',
            roomUrl: payload.roomUrl,
            startedAt: payload.startedAt
          });
        });
        connection.on('VideoCallEnded', () => setCallInfo(null));
        await connection.start();
        await connection.invoke('JoinAulaCall', aulaId);
        callConnectionRef.current = connection;
      } else if (connection.state !== HubConnectionState.Connected) {
        await connection.start();
        await connection.invoke('JoinAulaCall', aulaId);
      }

      const nombre = (nombreParam || participantDisplayName || 'Usuario').trim();
      const room = `${jitsiBase}/aula-particular-${aulaId}-${Date.now()}`;
      await connection.invoke('StartCall', aulaId, nombre, room);
      setCallInfo({
        startedBy: nombre,
        roomUrl: room,
        startedAt: new Date().toISOString()
      });
      window.open(room, '_blank', 'noopener,noreferrer');
      void emitTelemetry('aula.call_started', { roomUrl: room, startedBy: nombre });
    } catch (error) {
      setMessagesError(error.message || 'No pudimos iniciar la videollamada.');
    } finally {
      setCallConnecting(false);
    }
  };

  const handleOpenCallModal = () => {
    void iniciarVideollamada(participantDisplayName);
  };

  const handleJoinExistingCall = () => {
    if (!callInfo?.roomUrl) return;
    window.open(callInfo.roomUrl, '_blank', 'noopener,noreferrer');
    void emitTelemetry('aula.call_joined', { roomUrl: callInfo.roomUrl, startedBy: callInfo.startedBy });
  };

  const startBoardConnection = async () => {
    if (!aulaId || !participantDisplayName) {
      setMessagesError('No pudimos identificar el aula o el usuario para iniciar la pizarra.');
      return;
    }

    if (boardConnectionRef.current) {
      if (boardConnectionRef.current.state === HubConnectionState.Connected) {
        setBoardOpen(true);
        return;
      }

      try {
        await boardConnectionRef.current.stop();
      } catch (_) {}
      boardConnectionRef.current = null;
    }

    setBoardConnecting(true);
    setMessagesError('');
    let connection = null;
    try {
      connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/particular-aula-board`, {
          withCredentials: true,
          accessTokenFactory: getBearerToken
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('UserJoinedBoard', (name) => {
        if (typeof name === 'string' && name.startsWith('__observer__')) return;
        setBoardUsers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.includes(name) ? list : [...list, name];
        });
        if (!boardStartedByRef.current) {
          boardStartedByRef.current = name;
          setBoardStartedBy(name);
          setBoardStatusMsg(`${name} inició una pizarra colaborativa.`);
        }
      });
      connection.on('BoardState', (payload) => {
        const startedBy = payload?.startedBy || payload?.StartedBy;
        if (!startedBy) return;
        boardStartedByRef.current = startedBy;
        setBoardStartedBy(startedBy);
        setBoardStatusMsg(`${startedBy} inició una pizarra colaborativa.`);
      });
      connection.on('UserLeftBoard', (name) => {
        setBoardUsers((prev) => (Array.isArray(prev) ? prev.filter((item) => item !== name) : []));
      });
      connection.on('ConnectedUsersBoard', (users) => {
        applyBoardPresence(users);
      });
      connection.on('BoardAction', (payload) => {
        try {
          const data = JSON.parse(payload?.payload || '{}');
          if (payload?.autor !== 'system' && data.sourceId === boardActionSourceRef.current) return;
          handleRemoteBoardObject(data);
        } catch (_) {}
      });

      await connection.start();
      boardConnectionRef.current = connection;
      const hadActiveBoard = Boolean(boardStartedByRef.current || (Array.isArray(boardUsers) && boardUsers.length > 0));
      await connection.invoke('JoinBoard', aulaId, participantDisplayName);
      setBoardOpen(true);
      setBoardUsers((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.includes(participantDisplayName) ? list : [...list, participantDisplayName];
      });
      try {
        const users = await connection.invoke('GetConnectedUsers', aulaId);
        applyBoardPresence(users);
      } catch (_) {}
      window.setTimeout(() => {
        void sendBoardAction({ type: 'snapshot-request' });
      }, 250);
      void emitTelemetry(hadActiveBoard ? 'aula.board_joined' : 'aula.board_started', {
        startedBy: hadActiveBoard ? boardStartedByRef.current : participantDisplayName
      });
    } catch (error) {
      try {
        await connection?.stop();
      } catch (_) {}
      if (boardConnectionRef.current === connection) {
        boardConnectionRef.current = null;
      }
      setBoardOpen(false);
      setMessagesError(error.message || 'No pudimos conectar la pizarra.');
    } finally {
      setBoardConnecting(false);
    }
  };

  const leaveBoard = async () => {
    if (boardConnectionRef.current) {
      try {
        await boardConnectionRef.current.invoke('LeaveBoard', aulaId);
      } catch (_) {}
      try {
        await boardConnectionRef.current.stop();
      } catch (_) {}
      boardConnectionRef.current = null;
    }
    setBoardOpen(false);
    setBoardUsers([]);
    setBoardStartedBy('');
    setBoardStatusMsg('Nadie inició la pizarra aún.');
    disposeBoardCanvas();
  };

  const clearBoard = async () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    if (!window.confirm('¿Limpiar la pizarra para todos los participantes?')) return;
    instance.clear();
    instance.backgroundColor = '#ffffff';
    instance.renderAll();
    await sendBoardAction({ type: 'clear' });
    await sendBoardAction({ type: 'snapshot', snapshot: { objects: [], background: '#ffffff' } });
  };

  const resetBoardView = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    instance.setViewportTransform([1, 0, 0, 1, 0, 0]);
    instance.setZoom(1);
    syncBoardZoom(instance);
    instance.renderAll();
  };

  const addTextToBoard = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    const text = new fabric.IText('Escribí texto', {
      left: instance.getWidth() * 0.18,
      top: instance.getHeight() * 0.18,
      fill: boardColor,
      fontSize: boardTextSize,
      fontFamily: 'Inter, Manrope, Arial, sans-serif',
      fontWeight: '600',
      editable: true,
      selectable: true,
      evented: true
    });
    setBoardEraser(false);
    setBoardPanMode(false);
    setBoardSelectMode(true);
    instance.add(text);
    instance.setActiveObject(text);
    instance.requestRenderAll();
    sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(text) });
    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });
  };

  const addImageToBoard = (dataUrl) => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    fabric.Image.fromURL(dataUrl, (image) => {
      const scale = Math.min((instance.getWidth() * 0.7) / image.width, (instance.getHeight() * 0.7) / image.height, 1);
      image.set({
        left: instance.getWidth() * 0.15,
        top: instance.getHeight() * 0.15,
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        evented: true
      });
      instance.add(image);
      instance.setActiveObject(image);
      instance.renderAll();
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(image) });
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    });
  };

  const exportBoardImage = async (format = 'png') => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    setBoardExporting(true);
    try {
      const width = instance.getWidth();
      const height = instance.getHeight();
      const dataUrl = instance.toDataURL({ format: 'png', multiplier: 2 });
      if (format === 'png') {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `pizarra-particular-${aulaId || 'aula'}.png`;
        link.click();
      } else {
        const pdf = new jsPDF({
          orientation: width >= height ? 'l' : 'p',
          unit: 'px',
          format: [width, height]
        });
        pdf.addImage(dataUrl, 'PNG', 0, 0, width, height);
        pdf.save(`pizarra-particular-${aulaId || 'aula'}.pdf`);
      }
    } catch (_) {
      setMessagesError('No pudimos exportar la pizarra.');
    } finally {
      setBoardExporting(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadFile = async (file) => {
    try {
      const fileId = file.id || file.Id;
      const fileName = file.nombreArchivo || file.NombreArchivo || 'archivo';
      const blob = await particularesApiService.downloadAulaArchivo(aulaId, fileId);
      downloadBlob(blob, fileName);
      void emitTelemetry('aula.file_downloaded', { fileId, fileName, downloadType: 'single' });
    } catch (error) {
      setFilesError(error.message || 'No pudimos descargar el archivo.');
    }
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await particularesApiService.downloadAulaArchivoZip(aulaId);
      downloadBlob(blob, `${aulaNombre.replace(/\s+/g, '-')}-archivos.zip`);
      void emitTelemetry('aula.file_downloaded', { downloadType: 'zip' });
    } catch (error) {
      setFilesError(error.message || 'No pudimos descargar el ZIP.');
    }
  };

  const handleSendReply = async () => {
    if (!replyParent || !replyHtml.trim()) return;
    setReplySending(true);
    setMessagesError('');
    try {
      await particularesApiService.createAulaMensaje(aulaId, {
        Titulo: `Re: ${replyParent.titulo || replyParent.Titulo || 'Mensaje'}`,
        ContenidoHtml: replyHtml.trim(),
        IdMensajePadre: replyParent.id || replyParent.Id,
        Activo: true
      });
      await loadMessages(messagesSearch);
      setReplyParent(null);
      setReplyHtml('');
      void emitTelemetry('aula.foro_reply_created', {
        titulo: `Re: ${replyParent.titulo || replyParent.Titulo || 'Mensaje'}`
      });
    } catch (error) {
      setMessagesError(error.message || 'No pudimos enviar la respuesta.');
    } finally {
      setReplySending(false);
    }
  };

  const handleCreateMessage = async () => {
    if (!newMessageHtml.trim()) return;
    setNewMessageSending(true);
    setMessagesError('');
    try {
      await particularesApiService.createAulaMensaje(aulaId, {
        Titulo: newMessageTitle.trim() || undefined,
        ContenidoHtml: newMessageHtml.trim(),
        Activo: true
      });
      await loadMessages(messagesSearch);
      setNewMessageOpen(false);
      setNewMessageTitle('');
      setNewMessageHtml('');
      void emitTelemetry('aula.foro_reply_created', {
        titulo: newMessageTitle.trim() || 'Nuevo mensaje'
      });
    } catch (error) {
      setMessagesError(error.message || 'No pudimos publicar el mensaje.');
    } finally {
      setNewMessageSending(false);
    }
  };

  const handleOpenAssistant = (assistant) => {
    const url = getAssistantUrl(assistant);
    if (!url) return;
    const metadata = {
      assistantId: getAssistantId(assistant),
      assistantName: getAssistantName(assistant),
      assistantType: 'gpt_clasico'
    };
    window.open(url, '_blank', 'noopener,noreferrer');
    void emitTelemetry('aula.assistant_opened', metadata);
    void emitTelemetry('aula.chat_started', metadata);
  };

  const renderAssistantPanel = () => (
    <div className="aula-chat-layout">
      <aside className="assistant-list">
        <div className="assistant-list-header">
          <h3>Asistentes IA disponibles</h3>
          <span>{assignedAssistants.length}</span>
        </div>
        <div className="assistant-items">
          {assignedAssistants.length === 0 && (
            <div className="assistant-empty">
              <p>No hay asistentes IA disponibles en esta aula.</p>
            </div>
          )}
          {assignedAssistants.map((assistant) => (
            <button
              key={getAssistantId(assistant)}
              type="button"
              className={`assistant-item ${getAssistantId(selectedAssistant) === getAssistantId(assistant) ? 'active' : ''}`}
              onClick={() => setSelectedAssistant(assistant)}
            >
              <div>
                <h4>{getAssistantName(assistant)}</h4>
                <p>{getAssistantDescription(assistant)}</p>
              </div>
              <i className="fas fa-robot" aria-hidden="true" />
            </button>
          ))}
        </div>
      </aside>

      {selectedAssistant ? (
        <div className="assistant-detail">
          <div className="assistant-detail-header">
            <h2>{getAssistantName(selectedAssistant)}</h2>
            <p>{getAssistantDescription(selectedAssistant)}</p>
          </div>
          <button
            className="primary-cta"
            type="button"
            disabled={!getAssistantUrl(selectedAssistant)}
            onClick={() => handleOpenAssistant(selectedAssistant)}
          >
            <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
            Abrir Asistente IA
          </button>
        </div>
      ) : (
        <div className="assistant-detail assistant-detail-empty">
          <div>
            <h3>Selecciona un asistente</h3>
            <p>Elegí un asistente IA para abrirlo y trabajar dentro del aula.</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderMessageTree = (list, level = 0) =>
    (list || []).map((message, index) => {
      const messageId = message.id || message.Id;
      const respuestas = message.respuestas || message.Respuestas || [];
      const expanded = expandedMessages.has(messageId);

      return (
        <div
          key={messageId}
          className={`aula-message-item ${index % 2 === 0 ? 'alt' : ''}`}
          style={{ marginLeft: level * 12 }}
        >
          <div className="aula-message-header">
            <div className="aula-message-meta">
              <span className="aula-message-title">{message.titulo || message.Titulo || 'Mensaje'}</span>
              <span className="aula-message-author">
                {message.nombreRemitente || message.NombreRemitente || message.autorNombre || message.AutorNombre || 'Usuario'}
              </span>
              <span className="aula-message-date">{formatDateTime(message.fechaAlta || message.FechaAlta)}</span>
            </div>
            <div className="message-actions">
              {respuestas.length > 0 && (
                <button
                  type="button"
                  className="reply-toggle"
                  onClick={() => {
                    setExpandedMessages((prev) => {
                      const next = new Set(prev);
                      if (next.has(messageId)) {
                        next.delete(messageId);
                      } else {
                        next.add(messageId);
                      }
                      return next;
                    });
                  }}
                >
                  <span className="badge">{respuestas.length}</span>
                  {expanded ? 'Ocultar respuestas' : 'Ver respuestas'}
                </button>
              )}
              <button
                type="button"
                className="reply-chip"
                onClick={() => {
                  setReplyParent(message);
                  setReplyHtml('');
                }}
              >
                Responder
              </button>
            </div>
          </div>
          <div
            className="aula-message-body"
            dangerouslySetInnerHTML={{ __html: message.contenidoHtml || message.ContenidoHtml || '' }}
          />
          {respuestas.length > 0 && expanded && (
            <div className="aula-replies-block">
              {renderMessageTree(respuestas, level + 1)}
            </div>
          )}
        </div>
      );
    });

  const navItems = aula ? [
    { key: 'inicio', label: 'Inicio', icon: 'fa-house' },
    assistantsEnabled && { key: 'asistentes', label: 'Asistentes IA', icon: 'fa-robot', description: 'Abrí los asistentes asignados al aula.', count: assignedAssistants.length },
    filesEnabled && { key: 'archivos', label: 'Archivos', icon: 'fa-folder-open', description: 'Material para descargar.', count: files.length },
    linksEnabled && { key: 'links', label: 'Links', icon: 'fa-link', description: 'Recursos web compartidos.', count: links.length },
    boardEnabled && { key: 'pizarra', label: 'Pizarra', icon: 'fa-chalkboard', description: boardStatusMsg, live: boardOpen || Boolean(boardStartedBy) },
    chatEnabled && { key: 'chat', label: 'Chat', icon: 'fa-comments', description: !chatOpen && chatLive ? `${chatPresenceUsers.length} conectados ahora.` : 'Conversación en vivo del aula.', live: chatLive },
    videoEnabled && {
      key: 'video',
      label: 'Videollamada',
      icon: 'fa-video',
      description: callInfo ? `Sala abierta por ${callInfo.startedBy}` : 'Iniciar o unirse a la sala en vivo.',
      live: Boolean(callInfo)
    },
    messagesEnabled && { key: 'foro', label: 'Foro', icon: 'fa-bullhorn', description: 'Mensajes y respuestas del aula.', count: messagesTotal }
  ].filter(Boolean) : [];
  const currentSection = navItems.some((item) => item.key === activeSection) ? activeSection : 'inicio';
  const goToSection = (key) => {
    setActiveSection(key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="aula-public-page aula-shell particular-aula-public-page">
      <header className="aula-public-header">
        <div className="header-brand">
          <img src={logoAnimacion} alt="aluxIA" />
          <div>
            <p>aluxIA</p>
            <span>Aula particular</span>
          </div>
        </div>
        <div className="header-info">
          {curso && <span>{getName(curso)}</span>}
          <button type="button" className="btn-secondary" onClick={() => navigate(backPath)}>
            Volver
          </button>
          <button type="button" className="btn-secondary" onClick={loadAula} disabled={isLoading}>
            {isLoading ? 'Actualizando...' : 'Actualizar'}
          </button>
          <UserSessionMenu
            user={user}
            onLogout={logout}
            userFallbackLabel={isAlumnoParticular ? 'Alumno' : 'Particular'}
            extraItems={isGeneralAdmin ? [{
              key: 'switch-particular',
              label: 'Cambiar particular',
              icon: 'fas fa-exchange-alt',
              onClick: handleChangeParticular
            }] : []}
          />
        </div>
      </header>

      <div className={`aula-layout ${navItems.length > 1 ? '' : 'no-nav'}`}>
        {navItems.length > 1 && <AulaNav items={navItems} active={currentSection} onSelect={goToSection} />}

        <main className="aula-public-content">
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
            />
          )}

          {isLoading && (
            <div className="aula-loading">
              <div className="spinner"></div>
              <p>Cargando aula...</p>
            </div>
          )}

          {!isLoading && !aula && (
            <div className="aula-error">
              <h3>No pudimos acceder al aula</h3>
              <p>Aula no disponible.</p>
            </div>
          )}

          {messagesError && <div className="aula-error small">{messagesError}</div>}

          {!isLoading && aula && (
            <>
              <div className="aula-home" hidden={currentSection !== 'inicio'}>
                <section className="aula-hero">
                  <span className="aula-hero-badge">
                    <i className="fas fa-user-graduate" aria-hidden="true" />
                    Aula particular
                  </span>
                  <h1>{aulaNombre}</h1>
                  {aulaDescripcion && <p className="aula-hero-desc">{aulaDescripcion}</p>}
                  <dl className="aula-hero-meta">
                    <div>
                      <dt>Curso</dt>
                      <dd>{curso ? getName(curso) : '—'}</dd>
                    </div>
                    <div>
                      <dt>Acceso</dt>
                      <dd>{aula?.nicknameUrl || aula?.NicknameUrl || '—'}</dd>
                    </div>
                    {canSeeEtiquetas && etiquetas.length > 0 && (
                      <div>
                        <dt>Etiquetas</dt>
                        <dd className="particular-tags">
                          {etiquetas.map((etiqueta) => <TagPill etiqueta={etiqueta} key={getId(etiqueta)} />)}
                        </dd>
                      </div>
                    )}
                  </dl>
                </section>
                <AulaOverview items={navItems} onSelect={goToSection} />
              </div>

              {assistantsEnabled && (
                <AulaSection
                  active={currentSection === 'asistentes'}
                  icon="fa-robot"
                  title="Asistentes IA"
                  subtitle="Elegí un asistente para abrirlo y trabajar dentro del aula."
                >
                  {renderAssistantPanel()}
                </AulaSection>
              )}

              {filesEnabled && (
                <AulaSection
                  active={currentSection === 'archivos'}
                  icon="fa-folder-open"
                  title="Archivos"
                  subtitle={`${files.length} archivos disponibles para descarga`}
                >
                  {filesLoading && (
                    <div className="aula-loading soft">
                      <div className="spinner"></div>
                      <p>Cargando archivos...</p>
                    </div>
                  )}
                  {!filesLoading && filesError && <div className="aula-error small">{filesError}</div>}
                  {!filesLoading && !filesError && (
                    <>
                      <div className="files-actions-row">
                        <button className="btn-primary" disabled={files.length === 0} onClick={handleDownloadZip}>
                          Descargar todos (zip)
                        </button>
                        <button className="btn-secondary" onClick={loadFiles}>
                          Actualizar lista
                        </button>
                      </div>
                      {files.length === 0 && (
                        <div className="assistant-empty">
                          <p>No hay archivos publicados en esta aula.</p>
                        </div>
                      )}
                      {files.length > 0 && (
                        <div className="files-list">
                          {files.map((file) => (
                            <div key={file.id || file.Id} className="file-row">
                              <i className="fas fa-file-lines file-icon" aria-hidden="true" />
                              <div className="file-meta">
                                <h4>{file.nombreArchivo || file.NombreArchivo}</h4>
                                <span>{formatDateTime(file.fechaAlta || file.FechaAlta)}</span>
                              </div>
                              <button className="btn-secondary" onClick={() => handleDownloadFile(file)}>
                                Descargar
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </AulaSection>
              )}

              {linksEnabled && (
                <AulaSection
                  active={currentSection === 'links'}
                  icon="fa-link"
                  title="Links"
                  subtitle={`${links.length} recursos compartidos para esta aula`}
                >
                  {linksLoading && (
                    <div className="aula-loading soft">
                      <div className="spinner"></div>
                      <p>Cargando links...</p>
                    </div>
                  )}

                  {!linksLoading && linksError && <div className="aula-error small">{linksError}</div>}

                  {!linksLoading && !linksError && (
                    <>
                      <div className="files-actions-row">
                        <button className="btn-secondary" onClick={loadLinks}>
                          Actualizar lista
                        </button>
                      </div>

                      {links.length === 0 && (
                        <div className="assistant-empty">
                          <p>Todavía no hay links publicados para esta aula.</p>
                        </div>
                      )}

                      {links.length > 0 && (
                        <div className="aula-links-public-list">
                          {links.map((link) => {
                            const normalized = {
                              id: link.id || link.Id,
                              url: link.url || link.Url,
                              descripcion: link.descripcion || link.Descripcion || ''
                            };
                            const hostname = getLinkHostname(normalized.url);
                            return (
                              <article key={normalized.id} className="aula-link-public-card">
                                <div className="aula-link-public-copy">
                                  <div className="aula-link-public-eyebrow">
                                    <span>{hostname || 'Recurso web'}</span>
                                  </div>
                                  <h4>{normalized.descripcion || 'Recurso externo del aula'}</h4>
                                  <p>{normalized.url}</p>
                                </div>
                                <div className="aula-link-public-actions">
                                  <button
                                    className="btn-primary"
                                    onClick={() => {
                                      setLinkViewerLoading(true);
                                      setSelectedLinkResource(normalized);
                                    }}
                                  >
                                    Abrir en el aula
                                  </button>
                                  <button className="btn-secondary" onClick={() => window.open(normalized.url, '_blank', 'noopener,noreferrer')}>
                                    Nueva pestaña
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </AulaSection>
              )}

              {boardEnabled && (
                <AulaSection
                  active={currentSection === 'pizarra'}
                  icon="fa-chalkboard"
                  title="Pizarra colaborativa"
                  subtitle={boardStatusMsg}
                  status={boardOpen ? 'En vivo' : ''}
                  actions={(
                    <>
                      {!boardOpen && (
                        <button type="button" className="btn-primary" disabled={boardConnecting} onClick={startBoardConnection}>
                          {boardConnecting ? 'Conectando...' : boardStartedBy ? 'Unirse y participar' : 'Iniciar pizarra'}
                        </button>
                      )}
                      {boardOpen && <button type="button" className="btn-secondary" onClick={leaveBoard}>Salir</button>}
                    </>
                  )}
                >
                  {!boardOpen && (
                    <AulaEmptyState icon="fa-chalkboard">
                      Iniciá la pizarra o unite a la que ya está abierta para dibujar en tiempo real con el resto del aula.
                    </AulaEmptyState>
                  )}
                  {boardOpen && (
                    <div className="board-layout">
                      <div className="board-tools">
                        <label>Color</label>
                        <input type="color" value={boardColor} onChange={(event) => setBoardColor(event.target.value)} />
                        <label>Grosor</label>
                        <input type="range" min={1} max={16} value={boardWidth} onChange={(event) => setBoardWidth(Number(event.target.value))} />
                        <label>Tamaño texto</label>
                        <input type="range" min={12} max={72} value={boardTextSize} onChange={(event) => setBoardTextSize(Number(event.target.value))} />
                        {boardEraser && (
                          <>
                            <label>Tamaño goma</label>
                            <input type="range" min={4} max={40} value={boardEraserWidth} onChange={(event) => setBoardEraserWidth(Number(event.target.value))} />
                          </>
                        )}
                        <div className="board-mode-block">
                          <span className="board-mode-label">Modo de interacción</span>
                          <div className="board-toggle-row">
                            <button type="button" className={`btn-chip ${!boardEraser && !boardPanMode && !boardSelectMode ? 'active' : ''}`} onClick={() => { setBoardEraser(false); setBoardPanMode(false); setBoardSelectMode(false); }}>Dibujar</button>
                            <button type="button" className={`btn-chip ${boardEraser ? 'active' : ''}`} onClick={() => { setBoardEraser(true); setBoardPanMode(false); setBoardSelectMode(false); }}>Borrar</button>
                            <button type="button" className={`btn-chip ${boardPanMode ? 'active' : ''}`} onClick={() => { setBoardPanMode(true); setBoardEraser(false); setBoardSelectMode(false); }}>Mover lienzo</button>
                            <button type="button" className={`btn-chip ${boardSelectMode ? 'active' : ''}`} onClick={() => { setBoardSelectMode(true); setBoardPanMode(false); setBoardEraser(false); }}>Editar objetos</button>
                          </div>
                        </div>
                        <div className="board-export">
                          <button type="button" className="btn-secondary" disabled={boardExporting} onClick={() => exportBoardImage('png')}>{boardExporting ? 'Preparando...' : 'Exportar PNG'}</button>
                          <button type="button" className="btn-secondary" disabled={boardExporting} onClick={() => exportBoardImage('pdf')}>{boardExporting ? 'Preparando...' : 'Exportar PDF'}</button>
                          <button type="button" className="btn-secondary" onClick={() => imageInputRef.current?.click()}>Insertar imagen</button>
                          <button type="button" className="btn-secondary" onClick={addTextToBoard}>Insertar texto</button>
                          <button type="button" className="btn-secondary" onClick={resetBoardView}>Centrar</button>
                          <button type="button" className="btn-secondary" onClick={clearBoard}>Limpiar</button>
                        </div>
                        <div className="board-users">
                          <h4>Conectados ({boardUsers.length})</h4>
                          <ul>
                            {boardUsers.length === 0 ? <li>Sin usuarios</li> : boardUsers.map((name, index) => (
                              <li key={`${name}-${index}`} className={name === participantDisplayName ? 'me' : ''}>
                                {name === participantDisplayName ? 'Yo' : name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="board-canvas" ref={boardContainerRef}>
                        <canvas ref={boardCanvasRef} />
                        <div className="board-floating-controls" role="group" aria-label="Controles de zoom de la pizarra">
                          <button type="button" className="board-floating-btn" onClick={() => zoomBoardByFactor(1.2)}>+</button>
                          <div className="board-zoom-indicator">{Math.round(boardZoom * 100)}%</div>
                          <button type="button" className="board-floating-btn" onClick={() => zoomBoardByFactor(1 / 1.2)}>-</button>
                        </div>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = (readerEvent) => {
                              if (readerEvent.target?.result) {
                                addImageToBoard(readerEvent.target.result.toString());
                              }
                            };
                            reader.readAsDataURL(file);
                            event.target.value = '';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </AulaSection>
              )}

              {chatEnabled && (
                <AulaSection
                  active={currentSection === 'chat'}
                  icon="fa-comments"
                  title="Chat del aula"
                  subtitle={!chatOpen && chatLive
                    ? `Chat en vivo: ${chatPresenceUsers.join(', ')}`
                    : 'Conversación en vivo con quienes están viendo esta aula.'}
                  status={chatOpen ? 'Activo' : chatLive ? 'En vivo' : ''}
                  actions={(
                    <button type="button" className="btn-chat-aula" onClick={startChatConnection} disabled={chatConnecting || chatOpen}>
                      {chatConnecting ? 'Conectando...' : chatOpen ? 'Chat activo' : chatLive ? 'Unirse al chat' : 'Entrar al chat'}
                    </button>
                  )}
                >
                  {!chatOpen && (
                    <AulaEmptyState icon="fa-comments">
                      {chatLive
                        ? 'Hay un chat en vivo. Unite para conversar con quienes están conectados.'
                        : 'Entrá al chat para conversar en vivo y ver quiénes están conectados.'}
                    </AulaEmptyState>
                  )}
                  {chatOpen && (
                    <div className="chat-layout">
                      <aside className="chat-users">
                        <h4>Conectados ({chatUsers.length})</h4>
                        <ul>
                          {chatUsers.map((name, index) => (
                            <li key={`${name}-${index}`} className={name === participantDisplayName ? 'me' : ''}>
                              {name === participantDisplayName ? 'Yo' : name}
                            </li>
                          ))}
                        </ul>
                      </aside>
                      <div className="chat-window">
                        <div className="chat-messages" ref={chatScrollRef}>
                          {chatMessages.length === 0 ? (
                            <div className="assistant-empty"><p>No hay mensajes en esta sesión.</p></div>
                          ) : chatMessages.map((message, index) => (
                            <div
                              key={`${message.autor}-${message.fechaUtc}-${index}`}
                              className={`chat-bubble ${message.autor === participantDisplayName ? 'mine' : message.autor === 'sistema' ? 'system' : 'other'}`}
                            >
                              <div className="chat-meta">
                                <span className="chat-author">{message.autor === participantDisplayName ? 'Yo' : message.autor === 'sistema' ? 'Sistema' : message.autor}</span>
                                <span className="chat-time">{new Date(message.fechaUtc || Date.now()).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              <p>{message.contenido}</p>
                            </div>
                          ))}
                        </div>
                        <div className="chat-actions">
                          <div className="chat-compose">
                            <textarea
                              ref={chatInputRef}
                              rows={1}
                              placeholder="Escribe tu mensaje..."
                              value={chatInput}
                              onChange={(event) => {
                                setChatInput(event.target.value);
                                if (chatInputRef.current) {
                                  chatInputRef.current.style.height = 'auto';
                                  chatInputRef.current.style.height = `${Math.min(chatInputRef.current.scrollHeight, 140)}px`;
                                }
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                  event.preventDefault();
                                  void handleSendChatMessage();
                                }
                              }}
                            />
                            <button type="button" className="btn-primary btn-send" onClick={handleSendChatMessage} disabled={!chatInput.trim()}>
                              Enviar
                            </button>
                          </div>
                          <div className="chat-actions-secondary">
                            <button type="button" className="btn-secondary" onClick={leaveChat}>Salir</button>
                            <button type="button" className={`btn-secondary ${chatMuted ? 'btn-mute-on' : 'btn-mute-off'}`} onClick={() => setChatMuted((prev) => !prev)}>
                              {chatMuted ? 'Activar sonido' : 'Silenciar sonido'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              disabled={chatMessages.length === 0}
                              onClick={() => {
                                const lines = chatMessages.map((message) => {
                                  const who = message.autor === 'sistema' ? '[sistema]' : message.autor;
                                  return `${formatDateTime(message.fechaUtc || Date.now())} - ${who}: ${normalizeChatExportText(message.contenido)}`;
                                });
                                downloadBlob(new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' }), `chat-aula-particular-${aulaId}.txt`);
                              }}
                            >
                              Descargar conversación
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </AulaSection>
              )}

              {videoEnabled && (
                <AulaSection
                  active={currentSection === 'video'}
                  icon="fa-video"
                  title="Videollamada"
                  subtitle={callInfo ? `Sala abierta por ${callInfo.startedBy}` : 'Iniciar o unirse a la sala en vivo'}
                  status={callInfo ? 'Sala activa' : ''}
                >
                  <div className="aula-video-section">
                    {!callInfo && (
                      <div className="video-start-row">
                        <button type="button" className="btn-video-start" onClick={handleOpenCallModal} disabled={callConnecting}>
                          {callConnecting ? 'Conectando...' : 'Iniciar llamada grupal'}
                        </button>
                        <p className="video-hint">Se abrirá una sala para quienes están asociados a esta aula.</p>
                      </div>
                    )}
                    {callInfo && (
                      <div className="video-banner">
                        <div>
                          <h4>Videollamada grupal iniciada por {callInfo.startedBy}</h4>
                          <p>Se creó la sala. Unite para participar.</p>
                        </div>
                        <div className="video-actions">
                          <button type="button" className="btn-primary" onClick={handleJoinExistingCall}>Unirse</button>
                        </div>
                      </div>
                    )}
                  </div>
                </AulaSection>
              )}

              {messagesEnabled && (
                <AulaSection
                  active={currentSection === 'foro'}
                  icon="fa-bullhorn"
                  title="Foro del aula"
                  subtitle={`${messagesTotal} mensajes publicados`}
                  actions={(
                    <div className="messages-actions">
                      <input
                        type="text"
                        placeholder="Buscar en mensajes..."
                        value={messagesSearch}
                        onChange={(event) => setMessagesSearch(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            void loadMessages(event.target.value);
                          }
                        }}
                      />
                      <button className="btn-secondary" onClick={() => loadMessages(messagesSearch)} disabled={messagesLoading}>
                        {messagesLoading ? 'Cargando...' : 'Actualizar'}
                      </button>
                      <button className="btn-primary" onClick={() => setNewMessageOpen(true)} disabled={messagesLoading || newMessageOpen}>
                        Nuevo mensaje
                      </button>
                    </div>
                  )}
                >
                  {newMessageOpen && (
                    <div className="aula-reply-card">
                      <div className="aula-reply-header">
                        <div>
                          <h4>Nuevo mensaje</h4>
                        </div>
                        <button
                          className="btn-secondary"
                          onClick={() => {
                            setNewMessageOpen(false);
                            setNewMessageTitle('');
                            setNewMessageHtml('');
                          }}
                          disabled={newMessageSending}
                        >
                          Cancelar
                        </button>
                      </div>
                      <div className="aula-reply-form">
                        <label>Título</label>
                        <input
                          type="text"
                          value={newMessageTitle}
                          onChange={(event) => setNewMessageTitle(event.target.value)}
                          placeholder="Título del mensaje"
                        />
                        <label>Mensaje</label>
                        <textarea
                          rows={5}
                          value={newMessageHtml}
                          onChange={(event) => setNewMessageHtml(event.target.value)}
                          placeholder="Escribe tu mensaje..."
                        />
                        <button className="btn-primary" disabled={!newMessageHtml.trim() || newMessageSending} onClick={handleCreateMessage}>
                          {newMessageSending ? 'Publicando...' : 'Publicar mensaje'}
                        </button>
                      </div>
                    </div>
                  )}

                  {messagesLoading && (
                    <div className="aula-loading soft">
                      <div className="spinner"></div>
                      <p>Cargando mensajes...</p>
                    </div>
                  )}

                  {!messagesLoading && messages.length === 0 && (
                    <div className="assistant-empty">
                      <p>No hay mensajes publicados en esta aula.</p>
                    </div>
                  )}

                  {!messagesLoading && messages.length > 0 && (
                    <div className="aula-messages-list">{renderMessageTree(messages)}</div>
                  )}

                  {replyParent && (
                    <div className="aula-reply-card">
                      <div className="aula-reply-header">
                        <div>
                          <h4>Responder a: {replyParent.titulo || replyParent.Titulo || 'Mensaje'}</h4>
                        </div>
                        <button className="btn-secondary" onClick={() => { setReplyParent(null); setReplyHtml(''); }}>Cancelar</button>
                      </div>
                      <div className="aula-reply-form">
                        <label>Tu respuesta</label>
                        <textarea
                          rows={5}
                          value={replyHtml}
                          onChange={(event) => setReplyHtml(event.target.value)}
                          placeholder="Escribe tu respuesta..."
                        />
                        <button className="btn-primary" disabled={!replyHtml.trim() || replySending} onClick={handleSendReply}>
                          {replySending ? 'Enviando...' : 'Enviar respuesta'}
                        </button>
                      </div>
                    </div>
                  )}
                </AulaSection>
              )}
            </>
          )}
        </main>
      </div>

      {replySending && (
        <div className="fullscreen-overlay">
          <div className="spinner"></div>
          <p>Respondiendo mensaje...</p>
        </div>
      )}

      {callNameModalOpen && (
        <div className="consent-overlay">
          <div className="consent-card">
            <h3>Iniciar videollamada</h3>
            <p>Ingresa tu nombre para que los demás sepan quién abrió la sala.</p>
            <input
              value={callNameInput}
              onChange={(event) => setCallNameInput(event.target.value)}
              placeholder="Tu nombre"
            />
            <div className="consent-actions">
              <button className="btn-secondary" onClick={() => setCallNameModalOpen(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                disabled={!callNameInput.trim() && !participantDisplayName.trim()}
                onClick={async () => {
                  setCallNameModalOpen(false);
                  await iniciarVideollamada(callNameInput);
                }}
              >
                Crear sala
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedLinkResource && (
        <div className="consent-overlay link-viewer-overlay">
          <div className="link-viewer-modal">
            <div className="link-viewer-header">
              <div className="link-viewer-copy">
                <span className="link-viewer-eyebrow">Link compartido en el aula</span>
                <h3>{selectedLinkResource.descripcion || 'Recurso externo del aula'}</h3>
                <p>{selectedLinkResource.url}</p>
              </div>
              <div className="link-viewer-actions">
                <button className="btn-secondary" onClick={() => window.open(selectedLinkResource.url, '_blank', 'noopener,noreferrer')}>
                  Abrir en pestaña nueva
                </button>
                <button
                  className="link-viewer-close"
                  type="button"
                  aria-label="Cerrar visor de link"
                  onClick={() => {
                    setSelectedLinkResource(null);
                    setLinkViewerLoading(false);
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="link-viewer-note">
              <strong>Descripción:</strong>
              <span>{selectedLinkResource.descripcion || 'Este recurso no tiene una descripción adicional.'}</span>
            </div>

            <div className="link-viewer-frame-shell">
              {linkViewerLoading && (
                <div className="link-viewer-loading">
                  <div className="spinner"></div>
                  <p>Abriendo el recurso dentro del aula...</p>
                </div>
              )}
              <iframe
                title={selectedLinkResource.descripcion || 'Recurso externo del aula'}
                src={selectedLinkResource.url}
                className="link-viewer-frame"
                onLoad={() => setLinkViewerLoading(false)}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>

            <div className="link-viewer-footer">
              <p>Si el sitio no permite visualizarse embebido en el aula, usa el botón <strong>Abrir en pestaña nueva</strong>.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticularAulaPage;
